import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentList from './DocumentList';  // New import
import BalanceTable from './BalanceTable';  // New import
import Navbar from './Navbar';
import './Navbar.css';

function CandidateDetail() {
  const { acctNum: paramAcctNum } = useParams();  // Rename for clarity
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse acctNum early to validate
  const acctNum = paramAcctNum ? parseInt(paramAcctNum, 10) : NaN;
  console.log('Raw paramAcctNum:', paramAcctNum, 'Parsed acctNum:', acctNum);  // Debug: Log both

  useEffect(() => {
    const fetchData = async () => {
      if (isNaN(acctNum) || acctNum <= 0) {
        console.warn('Invalid acctNum, skipping fetch:', acctNum);  // Debug
        setError('Invalid account number');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        console.log(`Fetching candidate data for: ${paramAcctNum}`);  // Debug
        const response = await fetch(`/api/candidates/${paramAcctNum}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        console.log('Fetched candidate data:', result);  // Debug: Log response
        setData(result);

  // Compute fullName and set page title including office/district per spec
  const fullName = `${result.NameFirst || ''} ${result.NameMiddle ? result.NameMiddle + ' ' : ''}${result.NameLast || ''}`.trim();
  const officeDesc = result.OfficeDesc || result.Office || '';
  const juris = (result.Juris1num !== undefined && result.Juris1num !== null) ? String(result.Juris1num).trim() : '';
  const hasJuris = juris && juris.toLowerCase() !== 'none';
  const titleSuffix = officeDesc ? `Candidate for ${officeDesc}${hasJuris ? ' District ' + juris : ''}` : '';
  document.title = (fullName ? `${fullName}${titleSuffix ? ': ' + titleSuffix : ''}` : (titleSuffix || 'Candidate Detail'));
      } catch (err) {
        console.error('Fetch error:', err);  // Debug
        setError(err.message);
        document.title = 'Candidate Not Found';
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paramAcctNum, acctNum]);  // Depend on raw and parsed

  if (loading) return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <Navbar />
  <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        Loading candidate details...
      </main>
    </div>
  );

  if (error || !data || isNaN(acctNum)) return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />
  <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2>Candidate Not Found</h2>
          <button onClick={() => navigate(-1)}>Back to List</button>
        </section>
      </main>
    </div>
  );

  // Compute fullName for H1 and h3 text for office/district
  const fullName = `${data.NameFirst || ''} ${data.NameMiddle ? data.NameMiddle + ' ' : ''}${data.NameLast || ''}`.trim();
  const officeDesc = data.OfficeDesc || data.Office || '';
  const juris = (data.Juris1num !== undefined && data.Juris1num !== null) ? String(data.Juris1num).trim() : '';
  const hasJuris = juris && juris.toLowerCase() !== 'none';
  const h3Text = officeDesc ? `Candidate for ${officeDesc}${hasJuris ? ' — District ' + juris : ''}` : '';

  // Prepare fields for table (exclude _id, name parts; include computed address)
  const tableData = {
    ...data,
    fullAddress: `${data.Addr1 || ''}${data.Addr2 ? ', ' + data.Addr2 : ''}, ${data.City || ''}, ${data.State || ''} ${data.Zip || ''}`.trim()
  };
  delete tableData._id;

  const fields = Object.entries(tableData)
    .filter(([key]) => key !== 'NameFirst' && key !== 'NameMiddle' && key !== 'NameLast' && key !== 'AccountList')
    .map(([key, value]) => ({ key: key.replace(/([A-Z])/g, ' $1').trim(), value }));

  // Normalize AccountList to handle malformed data (e.g., { 'Campaign Account': 88746 } -> { accountName, accountNum })
  const normalizeAccountList = (rawList) => {
    if (!Array.isArray(rawList)) {
      console.warn('AccountList is not an array:', rawList);
      return [];
    }

    console.log('Raw AccountList from API data:', rawList);  // Debug: Log raw array
    console.log('Types in AccountList:', rawList.map(acc => ({ name: acc?.accountName || 'N/A', num: acc?.accountNum || 'N/A', numType: typeof (acc?.accountNum ?? acc?.[Object.keys(acc)?.[0] ?? '']) })));  // Debug: Types

    return rawList.map(acc => {
      if (!acc || typeof acc !== 'object') {
        console.warn('Skipping invalid account object:', acc);
        return null;
      }

      // Case 1: Standard schema { accountName, accountNum }
      if (acc.accountName !== undefined && acc.accountNum !== undefined) {
        const parsedNum = parseInt(acc.accountNum, 10);
        if (!isNaN(parsedNum) && parsedNum > 0) {
          return {
            accountName: acc.accountName,
            accountNum: parsedNum
          };
        }
      }

      // Case 2: Malformed { 'Campaign Account': 88746, _id? } or similar dynamic key
      const entries = Object.entries(acc).filter(([key]) => key !== '_id');
      if (entries.length === 1) {
        const [dynamicName, dynamicNum] = entries[0];
        const parsedNum = parseInt(dynamicNum, 10);
        if (!isNaN(parsedNum) && parsedNum > 0) {
          return {
            accountName: dynamicName,
            accountNum: parsedNum
          };
        }
      }

      // Case 3: Multi-key malformed? Log and skip (extend if needed for complex cases)
      console.warn('Unable to normalize account:', acc);
      return null;
    }).filter(Boolean);  // Remove nulls
  };

  // Safe accountList for BalanceTable (normalized)
  let safeAccountList = normalizeAccountList(data.AccountList || []);
  if (safeAccountList.length === 0) {
    // Fallback to single campaign account using candidate's AcctNum
    safeAccountList = [{ accountName: 'Campaign Account', accountNum: acctNum }];
  }
  console.log('Normalized safeAccountList for BalanceTable:', safeAccountList);  // Debug: Log passed prop

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

  <main style={{ maxWidth: '1000px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2 style={{ marginTop: 0 }}>{fullName}</h2>
          {h3Text && <h3 style={{ marginTop: 6, color: '#444', fontWeight: 500 }}>{h3Text}</h3>}

          <table border="1" style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid #ddd' }}>
            <tbody>
              {fields.map(({ key, value }, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ width: '30%', padding: '8px', backgroundColor: '#f9f9f9' }}>
                    <strong>{key}:</strong>
                  </td>
                  <td style={{ width: '70%', padding: '8px' }}>
                    {value || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Documents and balances */}
          <div style={{ marginTop: 16 }}>
            <DocumentList acctNum={acctNum} />
          </div>

          <div style={{ marginTop: 12 }}>
            <BalanceTable accountList={safeAccountList} />
          </div>

          <button onClick={() => navigate(-1)} style={{ marginTop: '20px' }}>Back to List</button>
        </section>
      </main>
    </div>
  );
}

export default CandidateDetail;